
import { staticConfig } from "../config/static";
import * as api from "../services/middleware/api.middleware";
import { requireActiveEnvironmentId } from "./environment.store";
import { getAuthHeader } from "./auth/authHeaders";
import { getScopeCustomerId } from "./activeScope";

const getEnvironmentId = () => requireActiveEnvironmentId();
const getCustomerId = () => getScopeCustomerId();
const getAccessToken = () => getAuthHeader();

export const getCatalogueFilteredData = (
    columns: any,
    filters: string,
    source: string[] = [],
    endpoint: string[] = [],
    page: number = 1,
    limit: number = 250,
    dataMartEntities: string[] = []
) => {

    const filtersParam = filters ? encodeURIComponent(filters) : '';

    const sourceParam = source.map(encodeURIComponent).join(',');
    const endpointParam = endpoint.map(encodeURIComponent).join(',');
    const dataMartParam = dataMartEntities.map(encodeURIComponent).join(',');
    const dataMartQuery = dataMartParam ? `&data_mart=${dataMartParam}` : "";

    const response = api.get<any>(
        `environment/${getEnvironmentId()}/data_catalogue_filtered?columns=${columns}&filters=${filtersParam}&source=${sourceParam}&endpoint=${endpointParam}${dataMartQuery}&page=${page}&limit=${limit}`,
        getAccessToken()
    );


    return response
}

export const getBronzeCatalogColumns = async ({
    sourceEntityId,
    page = 1,
    limit = 250,
}: {
    sourceEntityId: number;
    page?: number;
    limit?: number;
}) => {
    const environmentId = getEnvironmentId();
    const customerId = getCustomerId();

    if (!environmentId || !customerId) {
        return Promise.reject(new Error("Missing environment or customer context for bronze catalog request."));
    }

    const params = new URLSearchParams({
        environment_id: String(environmentId),
        customer_id: String(customerId),
        source_entity_id: String(sourceEntityId),
        page: String(page ?? 1),
        limit: String(limit ?? 250)
    });

    return api.get<any>(
        `${process.env.REACT_APP_API_URL}catalog/bronze-columns?${params.toString()}`,
        getAccessToken(),
        { options: { isUrlExclusive: true } }
    );
};

export const createInternalRegisteredDataSource = async ({
    data_source_name,
    data_source_description,
    is_active = true,
}: {
    data_source_name: string;
    data_source_description?: string | null;
    is_active?: boolean;
}) => {
    const environmentId = getEnvironmentId();

    return api.post<any>(
        `environments/${environmentId}/data_sources`,
        {
            data_source_name,
            data_source_description: data_source_description ?? null,
            data_source_type: "INTERNAL_REGISTERED",
            is_ingested: true,
            is_active,
        },
        getAccessToken()
    );
};

export const createInternalRegisteredSourceEntity = async ({
    payload,
}: {
    payload: {
        source_entity_name: string;
        source_entity_description?: string | null;
        source_schema: string;
        source_entity_layer: string;
        source_entity_kind: string;
        definition_sql?: string | null;
        is_active?: boolean;
        columns?: Array<{
            source_entity_column_name: string;
            data_type: string;
            source_entity_column_details?: {
                logical_name?: string | null;
                description?: string | null;
                constraint?: string | null;
                data_format?: string | null;
                example_values?: string | null;
                enable_genai?: boolean;
                pii?: boolean;
                classification?: string | null;
                owner?: string | null;
                notes?: string | null;
            };
        }>;
    };
}) => {
    const environmentId = getEnvironmentId();

    return api.post<any>(
        `environments/${environmentId}/source_entity/internal_registered`,
        payload,
        getAccessToken()
    );
};

// Fetch a single source entity in full (incl. source_schema + definition_sql, which the catalog
// list-fetch omits) so the edit modal can prefill every field with real values.
export const getSourceEntityForEdit = async ({
    dataSourceId,
    sourceEntityId,
}: {
    dataSourceId: number;
    sourceEntityId: number;
}) => {
    const environmentId = getEnvironmentId();
    return api.get<any>(
        `environments/${environmentId}/data_sources/${dataSourceId}/source_entity/${sourceEntityId}`,
        getAccessToken()
    );
};

// Edit an existing custom (internal-registered) source entity. PUTs to the generic source-entity
// update endpoint, which uses model_dump(exclude_unset=True) server-side. We send all entity-level
// fields (prefilled from getSourceEntityForEdit) but omit columns — those have their own manager.
export const updateInternalRegisteredSourceEntity = async ({
    dataSourceId,
    sourceEntityId,
    payload,
}: {
    dataSourceId: number;
    sourceEntityId: number;
    payload: {
        source_entity_name: string;
        source_entity_description?: string | null;
        source_schema?: string;
        source_entity_layer?: string;
        source_entity_kind?: string;
        definition_sql?: string | null;
        is_active?: boolean;
    };
}) => {
    const environmentId = getEnvironmentId();

    return api.put<any>(
        `environments/${environmentId}/data_sources/${dataSourceId}/source_entity/${sourceEntityId}`,
        payload,
        getAccessToken()
    );
};



export const updateCatalogueData = ({
    dataSourceId,
    sourceEntityId,
    sourceEntityColumnId,
    patch,
}: {
    dataSourceId: number;
    sourceEntityId: number;
    sourceEntityColumnId: number;
    patch: Record<string, any>;
}) => {
    const response = api.put<any>(
        `environments/${getEnvironmentId()}/data_sources/${dataSourceId}/source_entity/${sourceEntityId}/source_entity_column/${sourceEntityColumnId}`,
        patch,
        getAccessToken()
    )

    return response
}

export const deleteCatalogueSourceColumn = ({
    dataSourceId,
    sourceEntityId,
    sourceEntityColumnId,
}: {
    dataSourceId: number;
    sourceEntityId: number;
    sourceEntityColumnId: number;
}) => {
    return api.del<any>(
        `environments/${getEnvironmentId()}/data_sources/${dataSourceId}/source_entity/${sourceEntityId}/source_entity_column/${sourceEntityColumnId}`,
        getAccessToken(),
        getAccessToken(),
    );
}

export const createCatalogueSourceColumn = ({
    dataSourceId,
    sourceEntityId,
    payload,
}: {
    dataSourceId: number;
    sourceEntityId: number;
    payload: {
        source_entity_column_name: string;
        data_type: string;
        source_entity_column_details?: {
            logical_name?: string | null;
            description?: string | null;
            constraint?: string | null;
            data_format?: string | null;
            example_values?: string | null;
            enable_genai?: boolean;
            pii?: boolean;
            classification?: string | null;
            owner?: string | null;
            notes?: string | null;
        };
    };
}) => {
    return api.post<any>(
        `environments/${getEnvironmentId()}/data_sources/${dataSourceId}/source_entity/${sourceEntityId}/source_entity_column/single`,
        payload,
        getAccessToken()
    );
}

// Bulk-create several source-entity columns in one request. The backend endpoint accepts a list
// (List[SourceEntityColumnCreate]) and returns the created columns. Used by the multi-row Add Columns
// modal so a user can add many columns at once.
export type CreateSourceEntityColumnInput = {
    source_entity_column_name: string;
    data_type: string;
    source_entity_column_details?: {
        logical_name?: string | null;
        description?: string | null;
        constraint?: string | null;
        data_format?: string | null;
        example_values?: string | null;
        enable_genai?: boolean;
        pii?: boolean;
        classification?: string | null;
        owner?: string | null;
        notes?: string | null;
    };
};

export const createSourceEntityColumnsBulk = async ({
    dataSourceId,
    sourceEntityId,
    columns,
}: {
    dataSourceId: number;
    sourceEntityId: number;
    columns: CreateSourceEntityColumnInput[];
}) => {
    return api.post<any>(
        `environments/${getEnvironmentId()}/data_sources/${dataSourceId}/source_entity/${sourceEntityId}/source_entity_column`,
        columns,
        getAccessToken()
    );
};


export const getViewConfigurationsData = async (forSpecificView: boolean = null, selectedcatalogId: number = null) => {
    // const timestamp = new Date().getTime(); // Cache-busting query parameter
    const customer_id = getScopeCustomerId()
    const environment_id = getEnvironmentId();
    const url = forSpecificView && selectedcatalogId
        ? `${process.env.REACT_APP_API_URL}environment/${environment_id}/view_configuration/${selectedcatalogId}?customer_id=${customer_id}`
        : `${process.env.REACT_APP_API_URL}environment/${environment_id}/view_configuration_all/?customer_id=${customer_id}`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `${getAccessToken().Authorization}`,
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache',
                'Expires': '0'
            }
        });

        if (!response.ok) {
            throw new Error(`Network response was not ok: ${response.statusText}`);
        }

        const data = await response.json();
        return data;

    } catch (error) {
        console.error('Error fetching view configurations:', error);
        throw error;
    }
};

export const createView = async (viewName: string, selectedFields: string[], filterData: string, selectedDataSources: string[] = [], selectedEndpoints: string[] = []) => {
    const customer_id = getScopeCustomerId()
    const url = `environment/${getEnvironmentId()}/create_view/?customer_id=${customer_id}`;

    const requestData = {
        view_name: viewName,
        selected_fields: selectedFields,
        filter_data: filterData,
        selected_data_sources: selectedDataSources,
        selected_endpoints: selectedEndpoints
    };

    try {
        const getResponse = await api.post<any>(
            url,
            requestData,
            getAccessToken()
        );
        return getResponse.data;

    } catch (error) {
        console.error('Error creating view:', error);
        throw error;
    }
};

export const getDataMartMappings = async () => {
    const environmentId = getEnvironmentId();
    const customerId = getCustomerId();

    if (!environmentId || !customerId) {
        return Promise.reject(new Error("Missing environment or customer context for data mart mappings request."));
    }

    const params = new URLSearchParams({
        environment_id: String(environmentId),
        customer_id: String(customerId)
    });

    return api.get<any>(
        `${process.env.REACT_APP_API_URL}target-entity-dm-get?${params.toString()}`,
        getAccessToken(),
        { options: { isUrlExclusive: true } }
    );
};

export const getSilverCatalogColumns = async (targetEntityRdvId: number, limit: number = 250) => {
    const environmentId = getEnvironmentId();
    const customerId = getCustomerId();

    if (!environmentId || !customerId) {
        return Promise.reject(new Error("Missing environment or customer context for silver catalog request."));
    }

    const params = new URLSearchParams({
        environment_id: String(environmentId),
        customer_id: String(customerId),
        target_entity_rdv_id: String(targetEntityRdvId),
        limit: String(limit)
    });

    return api.get<any>(
        `${process.env.REACT_APP_API_URL}catalog/silver-columns?${params.toString()}`,
        getAccessToken(),
        { options: { isUrlExclusive: true } }
    );
};

export const getDataMartColumns = async ({
    targetEntityDmId,
    page = 1,
    limit = 250
}: {
    targetEntityDmId?: number | null;
    page?: number;
    limit?: number;
} = {}) => {
    const environmentId = getEnvironmentId();
    const customerId = getCustomerId();

    if (!environmentId || !customerId) {
        return Promise.reject(new Error("Missing environment or customer context for data mart columns request."));
    }

    const params = new URLSearchParams({
        environment_id: String(environmentId),
        customer_id: String(customerId),
        page: String(page ?? 1),
        limit: String(limit ?? 250)
    });

    if (typeof targetEntityDmId === "number") {
        params.set("target_entity_dm_id", String(targetEntityDmId));
    }

    return api.get<any>(
        `${process.env.REACT_APP_API_URL}target-entity-dm-columns?${params.toString()}`,
        getAccessToken(),
        { options: { isUrlExclusive: true } }
    );
};



export async function updateViewConfiguration(viewConfigId: number, selectedFields: string[], filters: string, viewName: string = undefined, selectedDataSources: string[] = [], selectedEndpoints: string[] = []) {

    const payload = {
        view_config_id: parseInt(`${viewConfigId}`),
        view_name: viewName,
        selected_fields: selectedFields,
        filters: filters || null,
        selected_data_sources: selectedDataSources,
        selected_endpoints: selectedEndpoints
    };

    const response = api.put<any>(
        `environment/${getEnvironmentId()}/update_view_configuration`,
        payload,
        getAccessToken()
    )

    return response
}


export const deleteView = async (viewId: number) => {
    const settings = {
        apiBaseUrl: staticConfig.apiBaseUrl,
    };

    const url = `${settings.apiBaseUrl}environment/${getEnvironmentId()}/delete_catalogue_view/${viewId}`;

    try {
        const response = await fetch(url, {
            method: 'DELETE',
            headers: {
                'Authorization': `${getAccessToken().Authorization}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Network response was not ok: ${response.statusText}`);
        }

        const getResponse = await response.json();

        return getResponse;

    } catch (error) {
        console.error('Error deleting view:', error);
        throw error;
    }
};
